/**
 * ReviewsPage — Marketing → Reviews (Google Business Profile Review Manager, Phase 1 MVP)
 *
 * Three tabs: Inbox · Connections · Service Issues
 *   - Inbox:        two-pane email-like UI. Left list of reviews, right pane for
 *                   the selected review and its draft (approve / edit / send / skip).
 *   - Connections:  per-Tillytics-location mapping to a Google Business Profile
 *                   location, plus brand-voice settings in a drawer.
 *   - Service Issues: list of auto-created issues from 1-3★ reviews.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Star, Megaphone, RefreshCw, Loader2, Link2, Unlink, Settings as SettingsIcon,
  CheckCircle2, Edit3, Send, SkipForward, X, AlertTriangle, Sparkles, Clock,
  MessageSquare, Filter, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAdmin } from '../context/AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../components/ui/sheet';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';

const StarRow = ({ rating }) => {
  const r = rating || 0;
  const color = r >= 4 ? 'text-emerald-500' : r === 3 ? 'text-amber-500' : 'text-rose-500';
  return (
    <div className={`inline-flex items-center gap-0.5 ${color}`} data-testid={`star-row-${r}`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className="w-3.5 h-3.5" fill={n <= r ? 'currentColor' : 'none'} />
      ))}
    </div>
  );
};

const STATE_BADGE = {
  none: { label: 'No reply yet', color: 'bg-slate-100 text-slate-700' },
  draft: { label: 'Draft awaiting approval', color: 'bg-amber-100 text-amber-800' },
  queued: { label: 'Auto-sending', color: 'bg-indigo-100 text-indigo-800' },
  sent: { label: 'Replied by Tillytics', color: 'bg-emerald-100 text-emerald-800' },
  skipped: { label: 'Skipped', color: 'bg-slate-100 text-slate-500' },
  replied_in_google: { label: 'Replied in Google', color: 'bg-sky-100 text-sky-800' },
  failed: { label: 'Send failed', color: 'bg-rose-100 text-rose-800' },
};

const prettyDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

// ─── Connections tab ─────────────────────────────────────────────────────────
const DemoDataCard = ({ api, locations, onDone }) => {
  const [locId, setLocId] = useState('');
  const [busy, setBusy] = useState(false);

  const seed = async () => {
    setBusy(true);
    try {
      const res = await api.post('/gbp/demo/seed', { tillytics_location_id: locId || null, auto_send_delay_min: 2 });
      toast.success(`Seeded ${res.data?.reviews_created} demo reviews with AI drafts`);
      await onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Seeding failed');
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Delete all demo reviews, demo service issues and the demo location mapping?')) return;
    setBusy(true);
    try {
      const res = await api.delete('/gbp/demo');
      toast.success(`Removed ${res.data?.reviews_deleted} demo reviews`);
      await onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clear failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-dashed" data-testid="gbp-demo-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" /> Demo reviews (platform admin)
        </CardTitle>
        <CardDescription>
          Seed sample Google reviews so you can try drafts, auto-send (2-minute delay for demo data) and service issues
          without Google API access. Demo replies are never posted to Google.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2 items-center">
        <Select value={locId} onValueChange={setLocId}>
          <SelectTrigger className="w-56" data-testid="gbp-demo-location-select">
            <SelectValue placeholder="First location (default)" />
          </SelectTrigger>
          <SelectContent>
            {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={seed} disabled={busy} data-testid="gbp-demo-seed-btn">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Seed demo reviews
        </Button>
        <Button variant="ghost" className="text-rose-600" onClick={clear} disabled={busy} data-testid="gbp-demo-clear-btn">
          Clear demo data
        </Button>
      </CardContent>
    </Card>
  );
};

const ConnectionsTab = ({ api, locations: tillyticsLocs, onChanged, status }) => {
  const { isPlatformAdmin } = useAdmin();
  const [connected, setConnected] = useState([]);
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [googleLocations, setGoogleLocations] = useState([]);
  const [chosenLoc, setChosenLoc] = useState(null);
  const [pickerForTilly, setPickerForTilly] = useState('');
  const [loading, setLoading] = useState(false);
  const [settingsForLoc, setSettingsForLoc] = useState(null); // open drawer for

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/gbp/locations');
      setConnected(res.data?.items || []);
    } catch {
      setConnected([]);
    }
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const connectGoogle = async (tillyticsLocationId = null) => {
    try {
      const res = await api.post('/gbp/oauth/start', { tillytics_location_id: tillyticsLocationId });
      if (res.data?.authorization_url) window.location.href = res.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start Google OAuth');
    }
  };

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/gbp/accounts');
      setGoogleAccounts(res.data?.accounts || []);
      if (res.data?.accounts?.length === 1) setAccountId(res.data.accounts[0].name.split('/')[1]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not list Google accounts');
    } finally {
      setLoading(false);
    }
  };

  const loadLocationsForAccount = async (id) => {
    setAccountId(id);
    setLoading(true);
    try {
      const res = await api.get(`/gbp/accounts/${id}/locations`);
      setGoogleLocations(res.data?.locations || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not list GBP locations');
      setGoogleLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const connectLocation = async () => {
    if (!chosenLoc || !pickerForTilly) {
      toast.error('Pick a Tillytics location and a Google location');
      return;
    }
    setLoading(true);
    try {
      await api.post('/gbp/locations/connect', {
        tillytics_location_id: pickerForTilly,
        account_name: `accounts/${accountId}`,
        location_name: chosenLoc.name,
        title: chosenLoc.title,
      });
      toast.success('Location connected — syncing last 90 days of reviews…');
      setChosenLoc(null);
      setPickerForTilly('');
      setGoogleLocations([]);
      setAccountId('');
      setGoogleAccounts([]);
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connect failed');
    } finally {
      setLoading(false);
    }
  };

  const disconnectLocation = async (tillyticsLocationId) => {
    if (!window.confirm('Disconnect this location? Historic reviews stay in your inbox but new ones will stop syncing.')) return;
    try {
      await api.post(`/gbp/locations/${tillyticsLocationId}/disconnect`);
      toast.success('Location disconnected');
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Disconnect failed');
    }
  };

  const disconnectOrg = async () => {
    if (!window.confirm('Fully disconnect Tillytics from your Google account? You can revoke access from your Google settings too.')) return;
    try {
      await api.post('/gbp/disconnect');
      toast.success('Disconnected');
      onChanged?.();
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Disconnect failed');
    }
  };

  const syncNow = async (tillyticsLocationId) => {
    try {
      await api.post(`/gbp/locations/${tillyticsLocationId}/sync-now`);
      toast.success('Sync complete');
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    }
  };

  const approvalPending = status?.connection?.google_approval_pending;

  return (
    <div className="space-y-4" data-testid="gbp-connections-tab">
      {!status?.oauth_configured && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6 text-sm text-amber-900">
            Google OAuth isn't configured on this server. Ask your admin to set
            <code className="mx-1 px-1 bg-white border rounded text-xs">GOOGLE_GBP_REDIRECT_URI</code>
            and matching Google Cloud OAuth client.
          </CardContent>
        </Card>
      )}

      {approvalPending && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-6 text-sm text-amber-900">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <div className="font-medium">Google Business Profile API — pending Google approval</div>
                <p className="mt-1">
                  Google blocked our request. Your Tillytics Google Cloud project needs
                  <strong> Basic API Access</strong> approved for the Business Profile APIs
                  before reviews can be read or replied to.
                </p>
                {status?.connection?.approval_note && (
                  <p className="mt-2 text-xs opacity-80">Last response: {status.connection.approval_note}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!status?.connected ? (
        <Card data-testid="gbp-connect-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" /> Connect Google Business Profile
            </CardTitle>
            <CardDescription>
              Sign in with the Google account that manages your café's listings.
              You'll then pick which Google location maps to each Tillytics location.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => connectGoogle()} data-testid="gbp-connect-google-btn">
              Connect Google
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Connected as {status.connection?.google_email || 'Google user'}
            </CardTitle>
            <CardDescription>
              Pick a Tillytics location below and map it to one of your Google Business
              Profile locations. You can map more than one.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadAccounts} disabled={loading} data-testid="gbp-load-accounts-btn">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Load my Google accounts
              </Button>
              <Button variant="ghost" onClick={disconnectOrg} className="text-rose-600" data-testid="gbp-disconnect-org-btn">
                <Unlink className="w-4 h-4 mr-2" /> Disconnect Google
              </Button>
            </div>

            {googleAccounts.length > 0 && (
              <div className="grid gap-2">
                <Label>Google account</Label>
                <div className="flex flex-wrap gap-2">
                  {googleAccounts.map((a) => (
                    <Button
                      key={a.name}
                      size="sm"
                      variant={accountId === a.name.split('/')[1] ? 'default' : 'outline'}
                      onClick={() => loadLocationsForAccount(a.name.split('/')[1])}
                      data-testid={`gbp-account-${a.name.split('/')[1]}`}
                    >
                      {a.accountName || a.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {googleLocations.length > 0 && (
              <div className="grid gap-3">
                <Label>Google Business Profile location</Label>
                <div className="space-y-1 max-h-72 overflow-auto border rounded-md">
                  {googleLocations.map((l) => (
                    <button
                      key={l.name}
                      onClick={() => setChosenLoc(l)}
                      className={`w-full text-left p-3 flex items-center justify-between hover:bg-accent transition
                        ${chosenLoc?.name === l.name ? 'bg-accent/60' : ''}`}
                      data-testid={`gbp-loc-${l.name}`}
                    >
                      <div>
                        <div className="font-medium">{l.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {l.storeCode || l.name}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>

                {chosenLoc && (
                  <div className="grid gap-3 p-3 border rounded-md bg-slate-50">
                    <div className="text-sm">
                      Map <strong>{chosenLoc.title}</strong> to the Tillytics location:
                    </div>
                    <Select value={pickerForTilly} onValueChange={setPickerForTilly}>
                      <SelectTrigger data-testid="gbp-tilly-loc-select">
                        <SelectValue placeholder="Pick a Tillytics location" />
                      </SelectTrigger>
                      <SelectContent>
                        {tillyticsLocs.map((l) => (
                          <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Button onClick={connectLocation} disabled={!pickerForTilly || loading} data-testid="gbp-save-mapping-btn">
                        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Save mapping
                      </Button>
                      <Button variant="ghost" onClick={() => setChosenLoc(null)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {connected.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Mapped locations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {connected.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-3 p-3 border rounded-md"
                data-testid={`gbp-connected-${c.tillytics_location_id}`}
              >
                <div>
                  <div className="font-medium">{c.tillytics_location_name || c.title}</div>
                  <div className="text-xs text-muted-foreground">
                    GBP: {c.title} · last synced: {c.last_synced_at ? prettyDate(c.last_synced_at) : 'never'}
                    {c.first_sync_pending && ' · initial sync queued'}
                  </div>
                </div>
                <div className="flex gap-2">
                  {c.is_demo && (
                    <Badge variant="outline" className="border-amber-400 text-amber-700" data-testid={`gbp-demo-badge-${c.tillytics_location_id}`}>Demo</Badge>
                  )}
                  <Badge variant={c.status === 'connected' ? 'default' : 'secondary'}>
                    {c.status}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => setSettingsForLoc(c)} data-testid={`gbp-settings-${c.tillytics_location_id}`}>
                    <SettingsIcon className="w-3.5 h-3.5 mr-1" /> Voice
                  </Button>
                  {!c.is_demo && (
                    <Button size="sm" variant="outline" onClick={() => syncNow(c.tillytics_location_id)} data-testid={`gbp-sync-${c.tillytics_location_id}`}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Sync
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => disconnectLocation(c.tillytics_location_id)} data-testid={`gbp-disconnect-${c.tillytics_location_id}`}>
                    <Unlink className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {isPlatformAdmin && (
        <DemoDataCard api={api} locations={tillyticsLocs} onDone={async () => { await refresh(); onChanged?.(); }} />
      )}

      <SettingsDrawer
        open={!!settingsForLoc}
        onOpenChange={(open) => !open && setSettingsForLoc(null)}
        gbpLoc={settingsForLoc}
        api={api}
        onSaved={refresh}
      />
    </div>
  );
};

// ─── Brand voice settings drawer ─────────────────────────────────────────────
const SettingsDrawer = ({ open, onOpenChange, gbpLoc, api, onSaved }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !gbpLoc) return;
    api.get(`/gbp/locations/${gbpLoc.tillytics_location_id}/settings`)
      .then((r) => setSettings(r.data?.settings))
      .catch(() => setSettings(null));
  }, [open, gbpLoc, api]);

  const save = async () => {
    if (!gbpLoc) return;
    setSaving(true);
    try {
      await api.put(`/gbp/locations/${gbpLoc.tillytics_location_id}/settings`, settings);
      toast.success('Settings saved');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Brand voice · {gbpLoc?.tillytics_location_name || gbpLoc?.title}</SheetTitle>
          <SheetDescription>
            Shape how Tillytics drafts replies to Google reviews for this location.
          </SheetDescription>
        </SheetHeader>
        {settings && (
          <div className="space-y-5 mt-6">
            <div className="space-y-2">
              <Label>Preset tone</Label>
              <Select
                value={settings.brand_voice_preset}
                onValueChange={(v) => setSettings({ ...settings, brand_voice_preset: v })}
              >
                <SelectTrigger data-testid="brand-voice-preset-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="friendly">Friendly</SelectItem>
                  <SelectItem value="warm">Warm</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="playful">Playful</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Your brand voice in one sentence</Label>
              <Textarea
                value={settings.brand_voice_text || ''}
                onChange={(e) => setSettings({ ...settings, brand_voice_text: e.target.value })}
                placeholder="e.g. warm Yorkshire, a little cheeky, mentions coffee by name"
                rows={3}
                data-testid="brand-voice-text"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Learn from past replies</Label>
                <p className="text-xs text-muted-foreground">
                  Use up to the 50 most recent owner replies on this GBP as examples.
                </p>
              </div>
              <Switch
                checked={!!settings.learn_from_past}
                onCheckedChange={(v) => setSettings({ ...settings, learn_from_past: v })}
                data-testid="learn-past-toggle"
              />
            </div>
            <Separator />
            <div className="space-y-2">
              <Label>Auto-send delay for 4-5★ (minutes)</Label>
              <Input
                type="number"
                min={0}
                max={240}
                value={settings.auto_send_delay_min}
                onChange={(e) => setSettings({ ...settings, auto_send_delay_min: Number(e.target.value || 0) })}
                data-testid="auto-send-delay"
              />
              <p className="text-xs text-muted-foreground">
                0 = send immediately. Any value gives you a window to cancel from the inbox.
              </p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Pause auto-send</Label>
                <p className="text-xs text-muted-foreground">4-5★ replies become drafts instead of auto-sending.</p>
              </div>
              <Switch
                checked={!!settings.pause_auto_send}
                onCheckedChange={(v) => setSettings({ ...settings, pause_auto_send: v })}
                data-testid="pause-auto-send-toggle"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Draft everything on this location</Label>
                <p className="text-xs text-muted-foreground">All ratings become drafts. Nothing auto-sends.</p>
              </div>
              <Switch
                checked={!!settings.override_draft_all}
                onCheckedChange={(v) => setSettings({ ...settings, override_draft_all: v })}
                data-testid="override-draft-all-toggle"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="brand-voice-save-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ─── Inbox tab ───────────────────────────────────────────────────────────────
const InboxTab = ({ api, connectedLocs, onChanged }) => {
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({});
  const [filterLoc, setFilterLoc] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const [filterRating, setFilterRating] = useState('all');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [acting, setActing] = useState(false);
  const selectedIdRef = useRef(null);
  const detailRef = useRef(null);
  useEffect(() => { selectedIdRef.current = selected?.id || null; }, [selected]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterLoc !== 'all') params.tillytics_location_id = filterLoc;
      if (filterState !== 'all') params.state = filterState;
      if (filterRating !== 'all') {
        params.min_rating = Number(filterRating);
        params.max_rating = Number(filterRating);
      }
      const res = await api.get('/gbp/reviews', { params });
      setReviews(res.data?.items || []);
      setCounts(res.data?.counts_by_state || {});
      // Preserve selection if the review still exists after refresh
      // (read via ref — `selected` would be a stale closure here)
      const selId = selectedIdRef.current;
      if (selId) {
        const match = (res.data?.items || []).find((r) => r.id === selId);
        if (match) {
          setSelected(match);
          setDraftText(match.draft_text || '');
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [api, filterLoc, filterState, filterRating]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  const openReview = (r) => {
    setSelected(r);
    setDraftText(r.draft_text || '');
    if (window.innerWidth < 1024) {
      setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  };

  const act = async (action, body = {}) => {
    if (!selected) return;
    setActing(true);
    try {
      const res = await api.post(`/gbp/reviews/${selected.id}/${action}`, body);
      if (res.data?.ok === false && res.data?.error) {
        toast.error(`Failed: ${res.data.error}`);
      } else {
        toast.success(res.data?.message || 'Done');
      }
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${action} failed`);
    } finally {
      setActing(false);
    }
  };

  const saveDraft = async () => act('edit', { text: draftText });
  const approve = async () => {
    if (draftText !== (selected?.draft_text || '')) {
      try {
        await api.post(`/gbp/reviews/${selected.id}/edit`, { text: draftText });
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not save draft');
        return;
      }
    }
    await act('approve');
  };

  if (!connectedLocs.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>Connect a Google Business Profile location to start seeing reviews.</p>
          <p className="text-xs mt-1">Head to the <strong>Connections</strong> tab above.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid="gbp-inbox-tab">
      {/* Left: filters + list */}
      <div className="lg:col-span-5 space-y-3">
        <Card>
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters</span>
              <Button variant="ghost" size="sm" className="ml-auto" onClick={refresh}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Select value={filterLoc} onValueChange={setFilterLoc}>
                <SelectTrigger data-testid="filter-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {connectedLocs.map((l) => (
                    <SelectItem key={l.tillytics_location_id} value={l.tillytics_location_id}>
                      {l.tillytics_location_name || l.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterRating} onValueChange={setFilterRating}>
                <SelectTrigger data-testid="filter-rating"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ratings</SelectItem>
                  <SelectItem value="5">5★</SelectItem>
                  <SelectItem value="4">4★</SelectItem>
                  <SelectItem value="3">3★</SelectItem>
                  <SelectItem value="2">2★</SelectItem>
                  <SelectItem value="1">1★</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterState} onValueChange={setFilterState}>
                <SelectTrigger data-testid="filter-state"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All states</SelectItem>
                  <SelectItem value="unreplied">Unreplied</SelectItem>
                  <SelectItem value="draft">Draft awaiting</SelectItem>
                  <SelectItem value="queued">Auto-sending</SelectItem>
                  <SelectItem value="sent">Replied (Tillytics)</SelectItem>
                  <SelectItem value="replied_in_google">Replied in Google</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-1 max-h-[70vh] overflow-y-auto">
          {reviews.length === 0 && !loading && (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                No reviews yet. New reviews land here within 15 minutes of Google receiving them.
              </CardContent>
            </Card>
          )}
          {reviews.map((r) => {
            const stateBadge = STATE_BADGE[r.draft_state] || STATE_BADGE.none;
            const isSelected = selected?.id === r.id;
            return (
              <button
                key={r.id}
                onClick={() => openReview(r)}
                data-testid={`review-row-${r.id}`}
                className={`w-full text-left p-3 border rounded-md transition
                  ${isSelected ? 'border-primary bg-primary/5' : 'hover:bg-accent'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <StarRow rating={r.star_rating} />
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${stateBadge.color}`}>
                    {stateBadge.label}
                  </span>
                </div>
                <div className="text-sm font-medium mt-1 truncate">
                  {r.reviewer_name || 'Anonymous'}
                </div>
                <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                  {r.comment || <em>(no written review)</em>}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {prettyDate(r.update_time)}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: detail / draft */}
      <div className="lg:col-span-7 scroll-mt-20" ref={detailRef}>
        {!selected ? (
          <Card>
            <CardContent className="py-16 text-center text-muted-foreground text-sm">
              Select a review to see its draft reply.
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="review-detail-panel">
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <StarRow rating={selected.star_rating} />
                    <span className="text-xs text-muted-foreground">
                      {prettyDate(selected.update_time)}
                    </span>
                  </div>
                  <CardTitle className="mt-1 text-lg">
                    {selected.reviewer_name || 'Anonymous reviewer'}
                  </CardTitle>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded ${STATE_BADGE[selected.draft_state]?.color || 'bg-slate-100'}`}>
                  {STATE_BADGE[selected.draft_state]?.label || selected.draft_state}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 border rounded-md bg-slate-50 text-sm whitespace-pre-wrap">
                {selected.comment || <em className="text-muted-foreground">(no written comment)</em>}
              </div>

              {selected.reply_text && selected.reply_source === 'google' && (
                <div className="p-3 border rounded-md bg-sky-50 text-sm">
                  <div className="font-medium text-sky-900 mb-1">Replied directly in Google</div>
                  <div className="whitespace-pre-wrap">{selected.reply_text}</div>
                </div>
              )}

              {selected.reply_text && selected.reply_source === 'tillytics' && (
                <div className="p-3 border rounded-md bg-emerald-50 text-sm">
                  <div className="font-medium text-emerald-900 mb-1">
                    Replied by Tillytics · {prettyDate(selected.reply_time)}
                  </div>
                  <div className="whitespace-pre-wrap">{selected.reply_text}</div>
                </div>
              )}

              {['none', 'draft', 'queued', 'failed'].includes(selected.draft_state) && (
                <>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      Draft reply
                      {selected.draft_state === 'queued' && selected.queued_send_at && (
                        <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Auto-sending at {prettyDate(selected.queued_send_at)}
                        </span>
                      )}
                    </Label>
                    <Textarea
                      value={draftText}
                      onChange={(e) => setDraftText(e.target.value)}
                      rows={6}
                      maxLength={1000}
                      placeholder="Draft your reply…"
                      data-testid="draft-textarea"
                    />
                    <div className="text-xs text-muted-foreground">
                      {draftText.length}/1000
                    </div>
                  </div>

                  {selected.last_error && (
                    <div className="p-2 border border-rose-200 rounded bg-rose-50 text-xs text-rose-800">
                      Last send attempt failed: {selected.last_error}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={approve}
                      disabled={acting || !draftText.trim()}
                      data-testid="approve-send-btn"
                    >
                      {acting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Approve & send
                    </Button>
                    <Button variant="outline" onClick={saveDraft} disabled={acting} data-testid="save-draft-btn">
                      <Edit3 className="w-4 h-4 mr-2" /> Save draft
                    </Button>
                    <Button variant="outline" onClick={() => act('regenerate')} disabled={acting} data-testid="regenerate-btn">
                      <RefreshCw className="w-4 h-4 mr-2" /> Regenerate
                    </Button>
                    {selected.draft_state === 'queued' && (
                      <Button variant="outline" onClick={() => act('cancel-auto-send')} disabled={acting} data-testid="cancel-queued-btn">
                        <X className="w-4 h-4 mr-2" /> Cancel auto-send
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => act('skip')} disabled={acting} data-testid="skip-btn">
                      <SkipForward className="w-4 h-4 mr-2" /> Skip
                    </Button>
                  </div>
                </>
              )}

              {selected.audit_log?.length > 0 && (
                <div className="pt-4 border-t">
                  <div className="text-xs font-medium text-muted-foreground mb-2">Audit trail</div>
                  <ul className="text-xs space-y-1 text-muted-foreground">
                    {[...selected.audit_log].reverse().slice(0, 8).map((a, i) => (
                      <li key={i}>
                        {prettyDate(a.at)} · {a.action}{a.actor_user_id ? '' : ' (Tillytics)'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ─── Service issues tab ──────────────────────────────────────────────────────
const COMMON_TAGS = ['staff', 'wait time', 'cleanliness', 'food quality', 'noise', 'pricing'];
const STATUS_OPTIONS = ['open', 'in_progress', 'resolved'];

const ServiceIssuesTab = ({ api }) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('all');
  const [editing, setEditing] = useState(null);
  const [newNote, setNewNote] = useState('');

  const refresh = useCallback(async () => {
    const params = {};
    if (status !== 'all') params.status = status;
    try {
      const res = await api.get('/gbp/service-issues', { params });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    }
  }, [api, status]);

  useEffect(() => { refresh(); }, [refresh]);

  const update = async (id, patch) => {
    try {
      await api.patch(`/gbp/service-issues/${id}`, patch);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="space-y-3" data-testid="gbp-service-issues-tab">
      <div className="flex items-center gap-2">
        <Label className="text-sm">Status:</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Nothing here — negative reviews auto-create a service issue so you can tag and track the root cause.
          </CardContent>
        </Card>
      ) : items.map((issue) => (
        <Card key={issue.id} data-testid={`issue-${issue.id}`}>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <StarRow rating={issue.star_rating} />
                  <Badge variant="outline">{issue.status}</Badge>
                </div>
                <CardTitle className="mt-1 text-base">
                  {issue.review?.reviewer_name || 'Anonymous reviewer'}
                </CardTitle>
                <CardDescription>{prettyDate(issue.created_at)}</CardDescription>
              </div>
              <Select value={issue.status} onValueChange={(v) => update(issue.id, { status: v })}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {issue.review?.comment && (
              <div className="p-2 border rounded bg-slate-50 text-sm whitespace-pre-wrap">
                {issue.review.comment}
              </div>
            )}
            <div>
              <Label className="text-xs">Tags</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {COMMON_TAGS.map((tag) => {
                  const on = (issue.tags || []).includes(tag);
                  return (
                    <button
                      key={tag}
                      onClick={() => {
                        const next = on
                          ? issue.tags.filter((t) => t !== tag)
                          : [...(issue.tags || []), tag];
                        update(issue.id, { tags: next });
                      }}
                      className={`text-[11px] px-2 py-0.5 rounded border transition
                        ${on ? 'bg-primary text-primary-foreground border-primary' : 'bg-white hover:bg-accent'}`}
                      data-testid={`tag-${issue.id}-${tag}`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
            {issue.notes?.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Notes</Label>
                {issue.notes.map((n, i) => (
                  <div key={i} className="p-2 border rounded text-xs">
                    <div className="text-muted-foreground">{prettyDate(n.at)}</div>
                    <div className="whitespace-pre-wrap mt-0.5">{n.text}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input
                placeholder="Add an internal note…"
                value={editing === issue.id ? newNote : ''}
                onChange={(e) => { setEditing(issue.id); setNewNote(e.target.value); }}
                onKeyDown={async (e) => {
                  if (e.key !== 'Enter' || editing !== issue.id || !newNote.trim()) return;
                  await update(issue.id, { add_note: newNote.trim() });
                  setNewNote('');
                  setEditing(null);
                }}
                data-testid={`note-input-${issue.id}`}
              />
              <Button
                variant="outline"
                size="sm"
                disabled={editing !== issue.id || !newNote.trim()}
                onClick={async () => {
                  await update(issue.id, { add_note: newNote.trim() });
                  setNewNote('');
                  setEditing(null);
                }}
                data-testid={`note-add-btn-${issue.id}`}
              >Add</Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ─── Page shell ──────────────────────────────────────────────────────────────
export default function ReviewsPage() {
  const { api, locations } = useAuth();
  const [tab, setTab] = useState('inbox');
  const [status, setStatus] = useState(null);
  const [connectedLocs, setConnectedLocs] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const refreshStatus = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get('/gbp/connection/status'),
        api.get('/gbp/locations'),
      ]);
      setStatus(s.data);
      setConnectedLocs((l.data?.items || []).filter((x) => x.status === 'connected'));
    } catch (e) {
      // treat as unconfigured
    }
    window.dispatchEvent(new Event('gbp-badge-refresh'));
  }, [api]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  // One-shot — if we just came back from OAuth, flash a toast and bounce to Connections.
  useEffect(() => {
    const flag = searchParams.get('gbp');
    const detail = searchParams.get('detail');
    if (!flag) return;
    if (flag === 'connected') {
      toast.success(detail ? `Connected: ${detail}` : 'Google connected', { id: 'gbp-oauth' });
    } else {
      toast.error(detail || `Google connect failed (${flag})`, { id: 'gbp-oauth' });
    }
    setTab('connections');
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="reviews-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-primary" /> Reviews
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Keep on top of every Google review — Tillytics drafts the replies, auto-sends the happy ones, and flags
            the unhappy ones so nothing slips.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs">
            Drafts: {status?.pending_drafts_count ?? '–'}
          </Badge>
          <Badge variant="outline" className="text-xs">
            Auto-queued: {status?.queued_auto_send_count ?? '–'}
          </Badge>
          <Badge variant="outline" className="text-xs">
            Open issues: {status?.open_service_issues_count ?? '–'}
          </Badge>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="reviews-tabs">
          <TabsTrigger value="inbox" data-testid="tab-inbox">Inbox</TabsTrigger>
          <TabsTrigger value="connections" data-testid="tab-connections">Connections</TabsTrigger>
          <TabsTrigger value="issues" data-testid="tab-issues">Service Issues</TabsTrigger>
        </TabsList>
        <TabsContent value="inbox" className="mt-4">
          <InboxTab api={api} connectedLocs={connectedLocs} onChanged={refreshStatus} />
        </TabsContent>
        <TabsContent value="connections" className="mt-4">
          <ConnectionsTab
            api={api}
            locations={locations}
            status={status}
            onChanged={refreshStatus}
          />
        </TabsContent>
        <TabsContent value="issues" className="mt-4">
          <ServiceIssuesTab api={api} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
